import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useEnvironment } from '../context/EnvironmentContext';
import { derivePageTitle, formatDocumentTitle, SANDBOX_TITLE_PREFIX } from '../utils/pageTitle';

// Re-exported so existing importers of this module keep working unchanged. Both moved to
// `web/utils/pageTitle.js` (card 41957) because they are pure and this module's React
// imports make it unloadable by the node-environment jest suite.
export { formatDocumentTitle, SANDBOX_TITLE_PREFIX };

/**
 * Sets `document.title` to the current route's own title, and keeps it prefixed with
 * `[SANDBOX] ` for the whole session in the sandbox environment (stripping the prefix
 * in live).
 *
 * Why a MutationObserver and not just an effect on `location`: pages across this
 * app set their own titles at their own times (on mount, after a fetch resolves,
 * inside a modal). An effect keyed on the route alone re-runs BEFORE those writes
 * and the prefix is then overwritten by the page — which reads as "the prefix only
 * works on some routes". Observing <title> re-applies it after whoever wrote last.
 *
 * The observer is disconnected around our own write, so setting the title cannot
 * re-trigger the callback that set it.
 *
 * ⚠️ The route title is applied ONCE per route, BEFORE the observer is created — the
 * observer re-applies only the `[SANDBOX] ` prefix, never the route map. That ordering
 * is deliberate: a page that sets its own title later still wins, which is the exact
 * behaviour the comment above depends on.
 */
export const useDocumentTitle = () => {
  const { isSandbox } = useEnvironment();
  const location = useLocation();

  useEffect(() => {
    const titleEl = document.querySelector('title');
    if (!titleEl) return undefined;

    let observer = null;

    const applyPrefix = () => {
      const next = formatDocumentTitle(document.title, isSandbox);
      if (next === document.title) return;
      observer?.disconnect();
      document.title = next;
      observer?.observe(titleEl, { childList: true, characterData: true, subtree: true });
    };

    document.title = derivePageTitle(location.pathname);

    observer = new MutationObserver(applyPrefix);
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    applyPrefix();

    return () => observer.disconnect();
  }, [isSandbox, location.pathname]);
};

export default useDocumentTitle;
